import { Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveStrategyType,
  type InspectionStrategy,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useGeckoLocale } from 'features/locale'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Edit3,
  Info,
  PenTool,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { CoverageCalculationBreakdown } from './CoverageCalculationBreakdown'
import { ZoneFloatingCard } from './ZoneFloatingCard'

const ZoneRowContainer = styled.div<{ $isDirty?: boolean }>`
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 2px solid
    ${({ theme, $isDirty }) => ($isDirty ? '#f97316' : theme.forecast.border)};
  overflow: hidden;
  transition: border-color 0.2s ease;
`

const ZoneMainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`

const ZoneInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const ZoneColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`

const ZoneName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1;
`

const ZoneActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionButton = styled.button<{
  $isDelete?: boolean
  $isActive?: boolean
  $activeColor?: string
}>`
  background: ${({ $isActive, $activeColor }) =>
    $isActive && $activeColor ? $activeColor : 'none'};
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isActive }) => ($isActive ? '#fff' : 'inherit')};
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme, $isDelete, $isActive, $activeColor }) => {
      if ($isDelete) return '#fee'
      if ($isActive && $activeColor) return $activeColor
      return theme.forecast.background3
    }};
    color: ${({ $isDelete, $isActive }) => {
      if ($isDelete) return '#dc2626'
      if ($isActive) return '#fff'
      return 'inherit'
    }};
  }
`

const BreakdownSection = styled.div`
  padding: 8px 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const BreakdownToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

export const ZoneRow: FC<{
  zone: ZoneParams
  totalAssetArea?: GeckoMeasurement
  onDelete?: () => void
  onUpdate?: (updates: Partial<ZoneParams>) => void
  onRedraw?: () => void
  onConfirm?: () => void
  onReject?: () => void
  readonly?: boolean
  showCoverageBreakdown?: boolean
}> = ({
  zone,
  totalAssetArea,
  onDelete,
  onUpdate,
  onRedraw,
  onConfirm,
  onReject,
  readonly,
  showCoverageBreakdown,
}) => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [breakdownExpanded, setBreakdownExpanded] = useState(false)
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { cof, confidence, hasChanges } = useZoneAnalysisData()

  // Clear dirty flag once the parent save completes (hasChanges goes false)
  useEffect(() => {
    if (!hasChanges) setIsDirty(false)
  }, [hasChanges])
  const areaUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.SquareMeters
  )

  const formattedArea = useMemo(() => {
    // Priority: override > calculated (asset model) > base area
    const areaSqM =
      zone.total_surface_area_sq_m_override ??
      zone.calculated_area_sq_m ??
      zone.total_surface_area_sq_m

    return areaSqM && areaSqM > 0
      ? GeckoMeasurement.CreateFromValue(
          areaSqM,
          GeckoMeasurementUnit.SquareMeters
        ).convertTo(areaUnit)
      : undefined
  }, [
    zone.total_surface_area_sq_m_override,
    zone.calculated_area_sq_m,
    zone.total_surface_area_sq_m,
    areaUnit,
  ])

  const zoneCoverage = useMemo(() => {
    // Use the same area priority as formattedArea
    const areaSqM =
      zone.total_surface_area_sq_m_override ??
      zone.calculated_area_sq_m ??
      zone.total_surface_area_sq_m

    if (!totalAssetArea || !areaSqM) return undefined
    const totalAssetAreaSqM = totalAssetArea.convertTo(
      GeckoMeasurementUnit.SquareMeters
    ).num
    return totalAssetAreaSqM > 0 ? (areaSqM / totalAssetAreaSqM) * 100 : 0
  }, [
    zone.total_surface_area_sq_m_override,
    zone.calculated_area_sq_m,
    zone.total_surface_area_sq_m,
    totalAssetArea,
  ])

  // Calculate required coverage for this zone using global CoF modifier and confidence from screening
  const requiredCoverage = useMemo(() => {
    return calculateRequiredCoverage(
      zone,
      getCofModifier(cof.level),
      confidence
    )
  }, [zone, cof.level, confidence])

  const stepId = useActiveStepId()
  const zoneColor = zone.color || '#3B82F6'
  const strategyType = getEffectiveStrategyType(zone.nii_strategy)

  return (
    <>
      <CommentIndicator anchor={{ type: 'zone', stepId, zoneId: zone.id }}>
        <ZoneRowContainer $isDirty={isDirty}>
          <ZoneMainRow>
            <ZoneInfo>
              <ZoneColorIndicator color={zoneColor} />
              <ZoneName>{zone.title}</ZoneName>
              {zone.ai_suggested && (
                <Tag icon={<Sparkles size={11} />} color='purple'>
                  AI suggested
                </Tag>
              )}
              {formattedArea ? (
                <Tag color='green'>
                  {formattedArea.toNumber(areaUnit).toFixed(2)} {areaUnit.name}
                </Tag>
              ) : (
                <Tag color='orange'>Area not calculated</Tag>
              )}
              {zoneCoverage !== undefined && (
                <Tag color='blue'>{zoneCoverage.toFixed(1)}% of asset</Tag>
              )}
              {requiredCoverage?.required_coverage_percent && (
                <Tag color='purple'>
                  {requiredCoverage.required_coverage_percent.toFixed(1)}%
                  required
                </Tag>
              )}
              {requiredCoverage?.required_coverage_area_sq_m && (
                <Tag color='geekblue'>
                  {requiredCoverage.required_coverage_area_sq_m.toFixed(2)} m²
                  to inspect
                </Tag>
              )}
              {getEffectiveStrategyType(zone.nii_strategy) && (
                <Tag
                  color={
                    zone.nii_strategy?.strategy_type_override
                      ? 'orange'
                      : 'cyan'
                  }>
                  Type {getEffectiveStrategyType(zone.nii_strategy)}
                  {zone.nii_strategy?.strategy_type_override && '*'}
                </Tag>
              )}
              {zone.damage_mechanisms && zone.damage_mechanisms.length > 0 && (
                <Tag color='orange'>
                  {zone.damage_mechanisms.length} DM
                  {zone.damage_mechanisms.length > 1 ? 's' : ''}
                </Tag>
              )}
              {showCoverageBreakdown && requiredCoverage && strategyType && (
                <BreakdownToggle
                  onClick={(event) => {
                    setBreakdownExpanded((prev) => !prev)
                    event.stopPropagation()
                    event.preventDefault()
                  }}
                  title={
                    breakdownExpanded
                      ? 'Hide coverage calculation'
                      : 'Show coverage calculation'
                  }>
                  {breakdownExpanded ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </BreakdownToggle>
              )}
            </ZoneInfo>
            {readonly ? (
              <ZoneActions>
                <ActionButton
                  $isActive={showDrawer}
                  $activeColor={zoneColor}
                  onClick={(event) => {
                    setShowDrawer((prev) => !prev)
                    event.stopPropagation()
                    event.preventDefault()
                  }}
                  title={
                    showDrawer ? 'Close zone details' : 'View zone details'
                  }>
                  <Info size={14} />
                </ActionButton>
              </ZoneActions>
            ) : (
              <ZoneActions>
                {zone.ai_suggested && onConfirm && (
                  <ActionButton
                    $isActive
                    $activeColor='#22C55E'
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      onConfirm()
                    }}
                    title='Confirm this AI-suggested zone'>
                    <Check size={14} />
                  </ActionButton>
                )}
                {zone.ai_suggested && (onReject ?? onDelete) && (
                  <ActionButton
                    $isDelete
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      ;(onReject ?? onDelete)?.()
                    }}
                    title='Reject this AI-suggested zone'>
                    <X size={14} />
                  </ActionButton>
                )}
                {onRedraw && (
                  <ActionButton
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      onRedraw()
                    }}
                    title={
                      zone.drawing_bounds
                        ? 'Redraw zone boundary'
                        : 'Draw zone boundary'
                    }>
                    <PenTool size={14} />
                  </ActionButton>
                )}
                <ActionButton
                  $isActive={showDrawer}
                  $activeColor={zoneColor}
                  onClick={(event) => {
                    setShowDrawer((prev) => !prev)
                    event.stopPropagation()
                    event.preventDefault()
                  }}
                  title={showDrawer ? 'Close zone editor' : 'Edit zone'}>
                  <Edit3 size={14} />
                </ActionButton>
                {!zone.ai_suggested && (
                  <ActionButton
                    $isDelete
                    onClick={onDelete}
                    title='Delete zone'>
                    <Trash2 size={14} />
                  </ActionButton>
                )}
              </ZoneActions>
            )}
          </ZoneMainRow>
          {showCoverageBreakdown &&
            breakdownExpanded &&
            requiredCoverage &&
            strategyType && (
              <BreakdownSection>
                <CoverageCalculationBreakdown
                  zone={zone}
                  coverage={requiredCoverage}
                  strategyType={strategyType as InspectionStrategy}
                  confidence={confidence}
                />
              </BreakdownSection>
            )}
        </ZoneRowContainer>
      </CommentIndicator>

      {/* Zone Floating Card */}
      {showDrawer && (readonly || onUpdate) && (
        <ZoneFloatingCard
          zone={zone}
          readonly={readonly}
          onClose={() => setShowDrawer(false)}
          onSave={
            onUpdate
              ? (updates) => {
                  setIsDirty(true)
                  onUpdate(updates)
                }
              : undefined
          }
        />
      )}
    </>
  )
}
